import { type FC } from 'react';
import { graphql, useFragment } from 'react-relay';
import { useDispatch } from 'react-redux';
import { FormattedMessage } from 'dibs-react-intl';
import { Button } from 'dibs-elements/exports/Button.tsx';
import { Link } from 'dibs-elements/exports/Link.tsx';
import { Spinner } from 'dibs-elements/exports/Spinner.tsx';
import { clearAllAppliedFiltersHandler } from '../SbSharedAppliedFilters/sbSharedAppliedFiltersHelpers.ts';
import { type HandleRefineMenuClose } from '../SbSharedRefineMenu/hooks/useRefineMenuClose.ts';
import { useSbSelector } from '../../reducers/useSbSelector.ts';
import { showResults } from '../sbMessages.tsx';

import styles from './SbSharedRefineMenuFooter.module.css';
import dibsCss from 'dibs-css';
import classnames from 'classnames';

import { type SbSharedRefineMenuFooter_itemSearch$key } from './__generated__/SbSharedRefineMenuFooter_itemSearch.graphql.ts';
import { eventNameConstants, trackEvent } from 'dibs-tracking';

const itemSearchFragment = graphql`
    fragment SbSharedRefineMenuFooter_itemSearch on ItemSearchQueryConnection {
        ...sbSharedAppliedFiltersHelpers_itemSearch
        totalResults
    }
`;

type Props = {
    itemSearch: SbSharedRefineMenuFooter_itemSearch$key;
    hideRefineMenu: HandleRefineMenuClose;
};

export const SbSharedRefineMenuFooter: FC<Props> = ({
    itemSearch: itemSearchRef,
    hideRefineMenu,
}) => {
    const dispatch = useDispatch();
    const itemSearch = useFragment(itemSearchFragment, itemSearchRef);
    const refetchInFlight = useSbSelector(state => state.filters.refetchInFlight);
    const onClearAllFilters = clearAllAppliedFiltersHandler(dispatch, itemSearch);
    const totalResults = itemSearch.totalResults ?? 0;
    return (
        <div
            className={classnames(
                dibsCss.frostedBackground,
                dibsCss.sticky,
                dibsCss.bottom0,
                dibsCss.zHigh,
                dibsCss.wFull,
                dibsCss.pSmall,
                dibsCss.flex
            )}
        >
            <Link
                onClick={event => {
                    if (!refetchInFlight) {
                        onClearAllFilters(event);
                        trackEvent({
                            eventName: eventNameConstants.EVENT_RESULTS_PAGE_REFINEMENT,
                            interaction_type: 'clear all filters',
                            step_interaction_name: 'filter modal',
                        });
                    }
                }}
                className={classnames(
                    refetchInFlight && styles.disabled,
                    dibsCss.textBodyMediumHeavy,
                    dibsCss.textLeft,
                    dibsCss.whitespaceNowrap
                )}
            >
                <FormattedMessage
                    id="sb.SbSharedRefineMenuFooter.clearAll"
                    defaultMessage="Clear All"
                />
            </Link>
            <Button
                className={classnames(styles.button, dibsCss.mlMedium, dibsCss.wFull)}
                size="medium"
                style="circle"
                onClick={() => hideRefineMenu({ areFiltersApplied: true })}
                disabled={refetchInFlight}
            >
                {refetchInFlight ? (
                    <span
                        className={classnames(
                            dibsCss.flex,
                            dibsCss.itemsCenter,
                            dibsCss.justifyCenter
                        )}
                    >
                        <Spinner black containerClass={styles.spinner} />
                    </span>
                ) : (
                    showResults(totalResults)
                )}
            </Button>
        </div>
    );
};
